import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { getService, services } from "@/data/services";
import { categories } from "@/data/categories";
import { clusters } from "@/data/clusters";
import { cases } from "@/data/cases";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

/* Sections accept `body` (string[]), `bullets` (string[]) and `table` (rows[]) */
const SectionBlock = ({ section }) => (
  <Reveal as="section" style={{ marginTop: "2.6rem" }}>
    <h2
      className="display"
      style={{
        fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
        margin: "0 0 1.1rem",
        letterSpacing: "-.01em",
        lineHeight: 1.15,
      }}
    >
      {section.h2}
    </h2>
    {(section.body || []).map((para, i) => (
      <p
        key={i}
        style={{
          color: "var(--ink-soft)",
          fontSize: "1.04rem",
          lineHeight: 1.75,
          margin: "0 0 1.1rem",
          maxWidth: "68ch",
        }}
      >
        {para}
      </p>
    ))}
    {section.bullets && (
      <ul
        className="cap-list"
        style={{ marginTop: ".6rem", maxWidth: "70ch" }}
        data-testid="service-bullets"
      >
        {section.bullets.map((b, i) => (
          <li key={i}>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
    {section.subsections && section.subsections.map((sub, si) => (
      <div key={si} style={{ marginTop: "1.6rem", maxWidth: "70ch" }} data-testid={`service-h3-${si}`}>
        <h3
          style={{
            fontFamily: "var(--display)",
            fontWeight: 400,
            fontSize: "1.18rem",
            margin: "0 0 .55rem",
            letterSpacing: "-.005em",
            lineHeight: 1.3,
            color: "var(--ink)",
          }}
        >
          {sub.h3}
        </h3>
        {(sub.body || []).map((para, i) => (
          <p
            key={i}
            style={{
              color: "var(--ink-soft)",
              fontSize: "1rem",
              lineHeight: 1.7,
              margin: "0 0 .9rem",
            }}
          >
            {para}
          </p>
        ))}
        {sub.bullets && (
          <ul className="cap-list" style={{ marginTop: ".3rem" }}>
            {sub.bullets.map((b, i) => (
              <li key={i}><span>{b}</span></li>
            ))}
          </ul>
        )}
      </div>
    ))}
    {section.table && (
      <div
        style={{
          marginTop: "1.6rem",
          border: "1px solid var(--line)",
          borderRadius: 2,
          overflow: "hidden",
          maxWidth: "100%",
        }}
        data-testid="service-table"
      >
        {section.table.map((row, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(140px, 220px) minmax(120px, 180px) 1fr",
              gap: "1.4rem",
              padding: "1.2rem 1.4rem",
              borderBottom:
                i < section.table.length - 1 ? "1px solid var(--line)" : "none",
              background: i % 2 === 0 ? "var(--paper-raised)" : "transparent",
            }}
          >
            {row.map((cell, j) => (
              <span
                key={j}
                className={j === 0 ? "mono" : ""}
                style={{
                  color: j === 0 ? "var(--brass-deep)" : "var(--ink)",
                  fontSize: j === 0 ? ".82rem" : "1rem",
                  letterSpacing: j === 0 ? ".06em" : "-.005em",
                  textTransform: j === 0 ? "uppercase" : "none",
                  fontFamily: j === 0 ? "var(--mono)" : "var(--body)",
                  lineHeight: 1.55,
                  fontWeight: j === 1 ? 600 : 400,
                }}
              >
                {cell}
              </span>
            ))}
          </div>
        ))}
      </div>
    )}
    {section.body2 && section.body2.map((para, i) => (
      <p
        key={`b2-${i}`}
        style={{
          color: "var(--ink-soft)",
          fontSize: "1.04rem",
          lineHeight: 1.75,
          margin: "1.4rem 0 0",
          maxWidth: "68ch",
        }}
      >
        {para}
      </p>
    ))}
  </Reveal>
);

export const ServiceDetail = () => {
  const { id } = useParams();
  const data = getService(id);

  useSeo({
    title: data ? data.metaTitle : "Service",
    description: data ? data.metaDescription : undefined,
    path: data ? `/services/${data.id}` : "/services",
    image: data?.image || "/assets/og.jpg",
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}/services/${data.id}#service`,
              name: data.name,
              description: data.intro,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: { "@type": "Country", name: "India" },
              serviceType: data.name,
              url: `${SITE_URL}/services/${data.id}`,
            },
            {
              "@type": "FAQPage",
              mainEntity: data.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
              { name: data.name, path: `/services/${data.id}` },
            ]),
          ],
        }
      : null,
    jsonLdId: "service-detail-jsonld",
  });

  if (!data) return <Navigate to="/services" replace />;

  // Cross-link picks (kept simple, deterministic)
  const linkedCategories = categories.slice(0, 6);
  const linkedClusters = clusters.slice(0, 4);
  const linkedCases = cases.slice(0, 3);
  const relatedServices = (data.related || [])
    .map((rid) => services.find((s) => s.id === rid))
    .filter(Boolean);

  const idx = services.findIndex((s) => s.id === id);
  const prev = services[(idx - 1 + services.length) % services.length];
  const next = services[(idx + 1) % services.length];

  return (
    <article data-testid={`service-detail-${data.id}`} className="service-detail">
      {/* HERO */}
      <header
        style={{
          position: "relative",
          minHeight: "62svh",
          background: "var(--ink)",
          color: "var(--paper)",
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${data.image || "/assets/og.jpg"}")`,
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
            filter: "grayscale(.1) brightness(.45) contrast(1.1)",
            transform: "scale(1.05)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(26,23,20,.3) 0%, rgba(26,23,20,.55) 55%, rgba(26,23,20,.97) 100%), linear-gradient(90deg, rgba(26,23,20,.85) 0%, rgba(26,23,20,.32) 60%, rgba(26,23,20,0) 100%)",
          }}
        />
        <div
          className="wrap"
          style={{
            position: "relative",
            zIndex: 2,
            paddingBlock: "calc(74px + var(--section)) clamp(2.5rem, 6vw, 5rem)",
            width: "100%",
          }}
        >
          <Reveal>
            <p
              className="eyebrow eyebrow-light"
              data-testid="service-eyebrow"
            >
              {data.eyebrow || "Service"}
            </p>
          </Reveal>
          <WordReveal
            as="h1"
            text={data.h1}
            className="display tp-title"
            stagger={50}
            testId="service-headline"
          />
          <Reveal
            as="p"
            delay={180}
            style={{
              maxWidth: "62ch",
              color: "rgba(239,234,224,.78)",
              marginTop: "1.4rem",
              fontSize: "1.1rem",
              lineHeight: 1.65,
            }}
          >
            {data.intro}
          </Reveal>
          <Reveal
            delay={300}
            style={{ marginTop: "2rem", display: "flex", flexWrap: "wrap", gap: ".8rem" }}
          >
            <Link
              to={`/contact?service=${encodeURIComponent(data.name)}`}
              className="btn btn-fill btn-lg btn-glow"
              data-testid="service-hero-enquiry"
            >
              <span>Start a {data.name.toLowerCase()} brief →</span>
            </Link>
            <Link
              to="/services"
              className="btn btn-ghost"
              style={{ borderColor: "rgba(239,234,224,.3)", color: "rgba(239,234,224,.85)" }}
              data-testid="service-back-to-services"
            >
              All services
            </Link>
          </Reveal>
        </div>
      </header>

      {/* FACTS */}
      {data.facts && data.facts.length > 0 && (
        <section
          className="wrap"
          style={{
            paddingBlock: "clamp(2.5rem, 5vw, 4rem)",
            borderBottom: "1px solid var(--line)",
          }}
          data-testid="service-facts"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: "1.6rem 2rem",
            }}
          >
            {data.facts.map(([k, v]) => (
              <Reveal key={k}>
                <span className="mono" style={{ color: "var(--brass-deep)" }}>
                  {k}
                </span>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontWeight: 380,
                    fontSize: "1.15rem",
                    margin: ".3rem 0 0",
                    letterSpacing: "-.005em",
                    lineHeight: 1.3,
                  }}
                >
                  {v}
                </p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* BODY (sections) */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)" }}
        data-testid="service-body"
      >
        {data.sections.map((s, i) => (
          <SectionBlock key={i} section={s} />
        ))}
      </section>

      {/* FAQ */}
      {data.faq && data.faq.length > 0 && (
        <section
          className="wrap"
          style={{
            paddingBlock: "var(--section)",
            borderTop: "1px solid var(--line)",
            background: "var(--paper-raised)",
          }}
          data-testid="service-faq"
        >
          <Reveal as="p" className="eyebrow">
            Frequently asked
          </Reveal>
          <WordReveal
            as="h2"
            text={`${data.name} — common questions.`}
            stagger={55}
            className="display"
          />
          <div style={{ marginTop: "2rem", maxWidth: "78ch" }}>
            {data.faq.map((f, i) => (
              <details
                key={i}
                className="faq-item"
                data-testid={`service-faq-${i}`}
              >
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* RELATED SERVICES + CATEGORIES + CLUSTERS */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="service-cross-links"
      >
        <Reveal as="p" className="eyebrow">
          Where this fits
        </Reveal>
        <WordReveal
          as="h2"
          text="Adjacent services, categories and clusters."
          stagger={50}
          className="display"
        />

        {/* Related services */}
        {relatedServices.length > 0 && (
          <div style={{ marginTop: "2.4rem" }}>
            <p
              className="mono"
              style={{
                fontSize: ".72rem",
                letterSpacing: ".12em",
                textTransform: "uppercase",
                color: "var(--brass-deep)",
                marginBottom: ".8rem",
              }}
            >
              Related services
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: ".9rem",
              }}
            >
              {relatedServices.map((r) => (
                <Link
                  key={r.id}
                  to={`/services/${r.id}`}
                  data-testid={`service-related-${r.id}`}
                  style={{
                    display: "block",
                    padding: "1.1rem 1.2rem",
                    border: "1px solid var(--line)",
                    borderRadius: 2,
                    transition: "all .2s ease",
                  }}
                  className="service-xlink"
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: ".68rem",
                      letterSpacing: ".1em",
                      textTransform: "uppercase",
                      color: "var(--brass-deep)",
                    }}
                  >
                    Service
                  </span>
                  <h3
                    style={{
                      fontFamily: "var(--display)",
                      fontWeight: 400,
                      fontSize: "1.05rem",
                      margin: ".35rem 0 0",
                      letterSpacing: "-.005em",
                      color: "var(--ink)",
                    }}
                  >
                    {r.name} →
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        <div style={{ marginTop: "2.4rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "var(--brass-deep)",
              marginBottom: ".8rem",
            }}
          >
            Product categories we cover
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: ".7rem",
            }}
          >
            {linkedCategories.map((c) => (
              <Link
                key={c.id}
                to={`/categories/${c.id}`}
                data-testid={`service-category-${c.id}`}
                style={{
                  padding: ".75rem 1rem",
                  border: "1px solid var(--line)",
                  borderRadius: 2,
                  fontFamily: "var(--display)",
                  fontWeight: 400,
                  color: "var(--ink)",
                  letterSpacing: "-.005em",
                  fontSize: ".98rem",
                  transition: "all .2s ease",
                }}
                className="service-xlink"
              >
                {c.name} →
              </Link>
            ))}
          </div>
        </div>

        {/* Clusters */}
        <div style={{ marginTop: "2.4rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "var(--brass-deep)",
              marginBottom: ".8rem",
            }}
          >
            Manufacturing clusters served
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: ".7rem",
            }}
          >
            {linkedClusters.map((c) => (
              <Link
                key={c.id}
                to={`/clusters/${c.id}`}
                data-testid={`service-cluster-${c.id}`}
                style={{
                  padding: ".75rem 1rem",
                  border: "1px solid var(--line)",
                  borderRadius: 2,
                  fontFamily: "var(--display)",
                  fontWeight: 400,
                  color: "var(--ink)",
                  letterSpacing: "-.005em",
                  fontSize: ".98rem",
                  transition: "all .2s ease",
                }}
                className="service-xlink"
              >
                {c.name} · {c.state} →
              </Link>
            ))}
          </div>
        </div>

        {/* Case studies */}
        <div style={{ marginTop: "2.4rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "var(--brass-deep)",
              marginBottom: ".8rem",
            }}
          >
            Selected case studies
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: ".9rem",
            }}
          >
            {linkedCases.map((cs) => (
              <Link
                key={cs.slug}
                to={`/work/${cs.slug}`}
                data-testid={`service-case-${cs.slug}`}
                style={{
                  padding: "1.1rem 1.2rem",
                  border: "1px solid var(--line)",
                  borderRadius: 2,
                  transition: "all .2s ease",
                  display: "block",
                }}
                className="service-xlink"
              >
                <span
                  className="mono"
                  style={{
                    fontSize: ".68rem",
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    color: "var(--brass-deep)",
                  }}
                >
                  Case study
                </span>
                <h3
                  style={{
                    fontFamily: "var(--display)",
                    fontWeight: 400,
                    fontSize: "1.02rem",
                    margin: ".35rem 0 0",
                    letterSpacing: "-.005em",
                    color: "var(--ink)",
                    lineHeight: 1.3,
                  }}
                >
                  {cs.title} →
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="cta wrap"
        style={{ borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow">
          Start an enquiry
        </Reveal>
        <Reveal as="h2" className="display">
          Brief us on {data.name.toLowerCase()}.
        </Reveal>
        <Reveal className="row">
          <Link
            to={`/contact?service=${encodeURIComponent(data.name)}`}
            className="btn btn-fill btn-lg btn-glow"
            data-testid="service-cta-enquiry"
          >
            <span>Send an enquiry →</span>
          </Link>
          <Link to="/services" className="btn btn-ghost">
            See all services
          </Link>
        </Reveal>
      </section>

      {/* PAGINATION */}
      <nav
        className="wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          paddingBlock: "2rem 4rem",
          borderTop: "1px solid var(--line)",
          fontFamily: "var(--mono)",
          fontSize: ".74rem",
          letterSpacing: ".08em",
          textTransform: "uppercase",
        }}
        aria-label="Service pagination"
        data-testid="service-pagination"
      >
        <Link
          to={`/services/${prev.id}`}
          className="btn btn-ghost"
          data-testid="service-prev"
        >
          ← {prev.name}
        </Link>
        <Link
          to={`/services/${next.id}`}
          className="btn btn-ghost"
          data-testid="service-next"
        >
          {next.name} →
        </Link>
      </nav>
    </article>
  );
};

export default ServiceDetail;
